<script lang="ts">
import { useRouter } from 'vue-router'
import { defineComponent } from 'vue'
import OrderDataService from '@/services/OrderDataService'
import type Order from '@/types/Order'
import type ResponseData from '@/types/ResponseData'
import type Pagination from '@/types/Pagination'
import * as _ from 'lodash'
import OrderInvoiceDataService from '@/services/OrderInvoiceDataService'
import type OrderInvoice from '@/types/OrderInvoice'
import * as FileSaver from 'file-saver'
import dayjs from 'dayjs'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

export default defineComponent({
  name: 'order-list',
  props: {
    userId: {
      type: [String, Number],
      default: null,
    },
    isPaidOnly: {
      type: Boolean,
      default: false,
    },
    filterStartDate: {
      type: String,
      default: '',
    },
    filterEndDate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      orders: [] as Order[],
      orderStatuses: [] as { id: number; description: string }[],
      singleOrderInvoice: {} as OrderInvoice,
      orderInvoices: [] as OrderInvoice[],
      currentOrder: {} as Order,
      router: useRouter(),
      searchTerm: '',
      loaded: false,
      selectedColumn: 'id',
      selectedDirection: 'desc',
      pagination: {} as Pagination,
      pagesRange: 5,
      selectedOrderId: null as number | null,
      selectedStatus: '',
      selectedOrderIds: [] as number[],
      bulkSelectedStatus: '',
      isBulkUpdating: false,
    }
  },
  methods: {
    formatDate(dateString: string) {
      const date = dayjs(dateString)
      return date.format('DD.MM.YYYY')
    },
    saveAs(filename: string, blob: Blob) {
      FileSaver.saveAs(blob, filename)
    },
    retrieveOrders(page: number, type: string) {
      this.selectedOrderIds = [];

      // If this is a paid-only view (order-profit page)
      if (this.isPaidOnly) {
        if (type === 'prev') page--;
        else if (type === 'next') page++;
        this.loaded = false;
        const filters = (this.filterStartDate && this.filterEndDate)
          ? { start_date: this.filterStartDate, end_date: this.filterEndDate }
          : undefined;
        OrderDataService.getPaidOrdersPaginated(page, filters)
          .then((response: ResponseData) => {
            this.orders = response.data.data.data || [];
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
            this.loaded = true;
          });
        return;
      }
      // If this is a user-specific view, fetch only that user's orders
      if (this.userId) {
        if (type === 'prev') {
          page--;
        } else if (type === 'next') {
          page++;
        }
        this.loaded = false;
        OrderDataService.getAllOrdersForUser(String(this.userId), page)
          .then((response: ResponseData) => {
            this.orders = response.data.data.data || [];
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
            this.loaded = true;
          });
        return;
      }
      // Unified search: search by ID, client, company name, and filter by status
      const searchValue = this.searchTerm;
      const statusValue = this.selectedStatus;
      console.log('[OrderTable] retrieveOrders called with:', searchValue, statusValue);
      this.loaded = false;
      // If searching by ID or text
      if (searchValue != null && searchValue !== 'all' && searchValue !== '') {
        const searchTerm = searchValue.trim();
        if (/^\d+$/.test(searchTerm)) {
          // Search by order ID (numeric)
          OrderDataService.getOrderById(Number(searchTerm))
            .then((response: ResponseData) => {
              let order = response.data.data;
              // Filter by status if selected
              if (order && statusValue && String(order.status) !== statusValue) order = null;
              this.orders = order ? [order] : [];
              this.pagination = { current_page: 1, last_page: 1 };
              this.loaded = true;
              if (!order) {
                toast.info('Objednávka s týmto ID nebola nájdená.');
              }
            })
            .catch(() => {
              this.orders = [];
              this.pagination = { current_page: 1, last_page: 1 };
              this.loaded = true;
              toast.info('Objednávka s týmto ID nebola nájdená.');
            });
        } else {
          // Search by client or company name
          OrderDataService.searchOrders(searchTerm)
            .then((response: ResponseData) => {
              let orders = response.data.data.data || [];
              // Filter by status if selected
              if (statusValue) {
                orders = orders.filter((o: Order) => String(o.status) === statusValue);
              }
              this.orders = orders;
              this.pagination = response.data.data || { current_page: 1, last_page: 1 };
              this.loaded = true;
              if (!this.orders.length) {
                toast.info('Žiadne objednávky neboli nájdené pre tento výraz.');
              }
            })
            .catch(() => {
              this.orders = [];
              this.pagination = { current_page: 1, last_page: 1 };
              this.loaded = true;
              toast.info('Žiadne objednávky neboli nájdené pre tento výraz.');
            });
        }
      } else if (searchValue === undefined) {
        this.orders = [];
        this.loaded = true;
      } else {
        if (type === 'prev') {
          page--;
        } else if (type === 'next') {
          page++;
        }
        OrderDataService.getAllOrders(page, 20, statusValue)
          .then((response: ResponseData) => {
            const orders = response.data.data.data;
            this.orders = orders;
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
            this.loaded = true;
          });
      }
    },
    refreshList() {
      this.retrieveOrders(1, '')
      this.currentOrder = {} as Order
    },
    downloadZFA(id: number) {
      const order = this.orders.find((value: Order) => value.id == id);
      if (order && order.amount > 0) {
        OrderInvoiceDataService.getOrderInvoiceForOrderByType(id, 1)
          .then((response: ResponseData) => {
            this.singleOrderInvoice = response.data.data
            OrderInvoiceDataService.generateInvoiceById(this.singleOrderInvoice.id).then((response: { data: string }) => {
              const byteCharacters = atob(response.data)
              const byteNumbers = new Array(byteCharacters.length)
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
              }
              const byteArray = new Uint8Array(byteNumbers)
              const blob = new Blob([byteArray], { type: 'application/pdf' })

              this.saveAs(this.singleOrderInvoice.file_name, blob)
            })
          })
          .catch((e: Error) => {
            toast.error('Sťahovanie zlyhalo: ' + e.message)
          })

      } else {
        toast.error('Sťahovanie nie je možné, FA neexistuje.');
      }

    },
    downloadFA(id: number) {
      const order = this.orders.find((value: Order) => value.id == id);
      if (order && order.amount > 0) {
        OrderInvoiceDataService.getOrderInvoiceForOrderByType(id, 2)
          .then((response: ResponseData) => {
            this.singleOrderInvoice = response.data.data
            OrderInvoiceDataService.generateInvoiceById(this.singleOrderInvoice.id).then((response: { data: string }) => {
              const byteCharacters = atob(response.data)
              const byteNumbers = new Array(byteCharacters.length)
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
              }
              const byteArray = new Uint8Array(byteNumbers)
              const blob = new Blob([byteArray], { type: 'application/pdf' })

              this.saveAs(this.singleOrderInvoice.file_name, blob)
            })
          })
          .catch((e: Error) => {
            toast.error('Sťahovanie zlyhalo: ' + e.message)
          })

      } else {
        toast.error('Sťahovanie nie je možné, FA neexistuje.');
      }

    },
    getOrderStatusName(statusId: number) {
      if (!statusId) return '-';
      const found = this.orderStatuses.find((s) => String(s.id) === String(statusId));
      return found ? found.description : statusId;
    },
    copyOrderId(id: number) {
      navigator.clipboard.writeText(String(id));
      this.selectedOrderId = id;
      setTimeout(() => { this.selectedOrderId = null }, 1000);
      toast.success('ID objednávky skopírované!');
    },
    getStatusBadgeClass(statusId: number) {
      // Example: color code by status
      switch (statusId) {
        case 1: return 'inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold';
        case 2: return 'inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold';
        case 3: return 'inline-block px-2 py-1 rounded bg-red-100 text-red-800 text-xs font-semibold';
        default: return 'inline-block px-2 py-1 rounded bg-gray-100 text-gray-800 text-xs font-semibold';
      }
    },
    clearAllFilters() {
      // Reset both filters and trigger watcher
      if (this.searchTerm !== '' || this.selectedStatus !== '') {
        this.searchTerm = '';
        this.selectedStatus = '';
        // Watchers will trigger retrieveOrders automatically
      }
    },
    orderMatchesSearch(order: Order, searchTerm: string) {
      if (!order || !searchTerm) return false;
      // Check company_name field and also company property if present
      if (order.company_name && order.company_name.toLowerCase().includes(searchTerm.toLowerCase())) return true;
      // Try to check company.name if present (for search API)
      if ((order as any).company && (order as any).company.name && (order as any).company.name.toLowerCase().includes(searchTerm.toLowerCase())) return true;
      return false;
    },
    getCompanyName(order: any) {
      if (!order) return '—';
      if (order.company_name && typeof order.company_name === 'string' && order.company_name.trim() !== '') {
        return order.company_name;
      }
      if (order.company && typeof order.company.name === 'string' && order.company.name.trim() !== '') {
        return order.company.name;
      }
      return '—';
    },
    allVisibleOrdersSelected() {
      if (!this.orders?.length) return false;
      return this.orders.every((order: Order) => this.selectedOrderIds.includes(order.id));
    },
    toggleSelectAllVisible(event: Event) {
      const target = event.target as HTMLInputElement;
      if (target.checked) {
        this.selectedOrderIds = this.orders.map((order: Order) => order.id);
      } else {
        this.selectedOrderIds = [];
      }
    },
    toggleOrderSelection(orderId: number, event: Event) {
      const target = event.target as HTMLInputElement;
      if (target.checked) {
        if (!this.selectedOrderIds.includes(orderId)) {
          this.selectedOrderIds.push(orderId);
        }
      } else {
        this.selectedOrderIds = this.selectedOrderIds.filter((id) => id !== orderId);
      }
    },
    async applyBulkStatusUpdate() {
      if (!this.selectedOrderIds.length) {
        toast.error('Chyba: Vyberte aspoň jednu objednávku.');
        return;
      }

      if (!this.bulkSelectedStatus) {
        toast.error('Chyba: Vyberte stav pre hromadnú zmenu.');
        return;
      }

      this.isBulkUpdating = true;
      try {
        const statusId = Number(this.bulkSelectedStatus);
        const response = await OrderDataService.bulkUpdateStatus(this.selectedOrderIds, statusId);
        const updatedCount = response?.data?.data?.updated_count ?? this.selectedOrderIds.length;
        const alreadyInStatusCount = response?.data?.data?.already_in_status_count ?? 0;

        toast.success(`Stav bol úspešne zmenený pre ${updatedCount} objednávok.`);
        if (alreadyInStatusCount > 0) {
          toast.info(`${alreadyInStatusCount} objednávok už malo tento stav.`);
        }

        this.selectedOrderIds = [];
        this.retrieveOrders(this.pagination.current_page || 1, '');
      } catch (e: any) {
        toast.error('Chyba: Hromadná zmena stavu zlyhala.');
      } finally {
        this.isBulkUpdating = false;
      }
    },
  },
  computed: {
    searchValue() {
      return this.searchTerm;
    },
    canBulkStatusUpdate() {
      return !this.userId && !this.isPaidOnly;
    },
    pages() {
      const currentPage = this.pagination.current_page;
      const lastPage = this.pagination.last_page;
      const startPage = Math.max(1, currentPage - Math.floor(this.pagesRange / 2));
      const endPage = Math.min(lastPage, startPage + this.pagesRange - 1);
      return _.range(startPage, endPage + 1);
    },
  },
  watch: {
    searchTerm() {
      this.retrieveOrders(1, '');
    },
    selectedStatus() {
      this.retrieveOrders(1, '');
    },
    filterStartDate() {
      this.retrieveOrders(1, '');
    },
    filterEndDate() {
      this.retrieveOrders(1, '');
    },
  },
  mounted() {
    OrderDataService.getOrderStatuses()
      .then((response) => {
        this.orderStatuses = response.data.data;
      })
      .catch(() => {
        this.orderStatuses = [];
      })
      .then(() => {
        this.retrieveOrders(1, '');
      });
  }
})
</script>

<template>
  <main class="flex-1 bg-gradient-to-br from-white via-slate-50 to-slate-100 min-h-screen">
    <div>
      <div class="max-w-7xl mx-auto">
        <div class="py-2">
          <div class="flex flex-col gap-4">
            <div class="overflow-x-auto rounded-2xl shadow-lg bg-white/80 backdrop-blur-md border border-gray-100">
              <div class="flex flex-col gap-4 mb-4 px-4 pt-4">
                <div class="flex flex-col lg:flex-row gap-4 w-full">
                  <div class="relative flex-1 w-full">
                    <label for="search" class="sr-only">Hľadať</label>
                    <div class="relative w-full">
                      <input v-model="searchTerm" type="search" name="search"
                        class="block w-full p-3 pl-12 pr-14 text-base border border-gray-200 rounded-2xl shadow focus:border-teal-600 focus:ring-2 focus:ring-teal-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 transition placeholder-gray-400 bg-gradient-to-r from-slate-50 to-slate-100"
                        placeholder="Hľadať podľa ID, klienta, spoločnosti alebo popisu..."
                        @keyup.enter="retrieveOrders(1, '')"
                        aria-label="Hľadať podľa ID, klienta, spoločnosti alebo popisu..." />
                      <svg
                        class="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-teal-400 pointer-events-none"
                        fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <!-- Removed custom clear button, using browser default -->
                    </div>
                  </div>
                  <div class="relative w-full lg:w-64">
                    <label for="status" class="sr-only">Stav objednávky</label>
                    <select v-model="selectedStatus" name="status" id="status"
                      class="block w-full p-3 text-sm border border-gray-200 rounded-2xl shadow focus:border-teal-600 focus:ring-2 focus:ring-teal-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 transition bg-gradient-to-r from-slate-50 to-slate-100">
                      <option value="">Všetky stavy</option>
                      <option v-for="status in orderStatuses" :key="status.id" :value="String(status.id)">
                        {{ status.description }}
                      </option>
                    </select>
                    <svg
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-teal-400 pointer-events-none"
                      fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path d="M19 9l-7 7-7-7" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </div>
                  <div class="flex items-center w-full lg:w-auto">
                    <button @click="clearAllFilters"
                      class="w-full lg:w-auto px-6 py-3 rounded-2xl bg-slate-200 hover:bg-teal-100 text-teal-700 font-semibold text-sm shadow border border-gray-200 transition whitespace-nowrap"
                      :disabled="!searchTerm && !selectedStatus">
                      Vymazať filtre
                    </button>
                  </div>

                  <div v-if="canBulkStatusUpdate" class="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                    <select v-model="bulkSelectedStatus" name="bulk-status" id="bulk-status"
                      class="block w-full sm:w-56 p-3 text-sm border border-gray-200 rounded-2xl shadow focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition bg-gradient-to-r from-slate-50 to-slate-100">
                      <option value="">Hromadná zmena stavu</option>
                      <option v-for="status in orderStatuses" :key="`bulk-${status.id}`" :value="String(status.id)">
                        {{ status.description }}
                      </option>
                    </select>
                    <button @click="applyBulkStatusUpdate"
                      class="w-full sm:w-auto px-6 py-3 rounded-2xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm shadow border border-teal-700 transition whitespace-nowrap disabled:opacity-50"
                      :disabled="!selectedOrderIds.length || !bulkSelectedStatus || isBulkUpdating">
                      {{ isBulkUpdating ? 'Ukladám...' : `Zmeniť stav (${selectedOrderIds.length})` }}
                    </button>
                  </div>

                </div>
              </div>
              <div v-if="!loaded"
                class="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                <div class="text-center">
                  <svg class="inline mr-2 w-12 h-12 text-gray-200 animate-spin fill-teal-500" viewBox="0 0 100 101"
                    fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor" />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill" />
                  </svg>
                  <div class="text-gray-700 mt-4 text-lg font-semibold">Načítavam objednávky...</div>
                </div>
              </div>
              <div class="columns flex flex-col items-center justify-center py-16" v-if="loaded && !orders?.length">
                <svg width="72" height="72" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  class="mb-4 text-gray-200">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 17v-2a4 4 0 018 0v2m-4-4a4 4 0 00-4-4V7a4 4 0 018 0v2a4 4 0 00-4 4z" />
                </svg>
                <p class="text-gray-400 text-lg font-medium">Žiadne objednávky neboli nájdené.</p>
              </div>
              <div class="p-2 w-full inline-block align-middle overflow-x-auto" v-if="loaded && orders?.length">
                <div class="overflow-hidden border border-gray-100 rounded-3xl shadow-2xl bg-white/95">
                  <table class="min-w-full divide-y divide-gray-100 text-[13px]">
                    <thead class="bg-gradient-to-r from-white via-slate-50 to-slate-100 sticky top-0 z-10">
                      <tr>
                        <th v-if="canBulkStatusUpdate" scope="col"
                          class="px-2 py-2 font-semibold text-left text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          <input type="checkbox" :checked="allVisibleOrdersSelected()" @change="toggleSelectAllVisible"
                            :disabled="!orders?.length"
                            class="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                        </th>
                        <th scope="col"
                          class="flex items-center px-2 py-2 font-semibold text-left text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          <span class="inline-flex items-center gap-1"
                            :class="{ 'text-teal-600': selectedColumn == 'id' }">ID
                            <button class="ml-1 text-slate-400 hover:text-teal-600 transition">
                              <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" aria-hidden="true"
                                fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col"
                          class="px-2 py-2 font-semibold text-left text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          <span class="inline-flex items-center gap-1"
                            :class="{ 'text-teal-600': selectedColumn == 'name' }">
                            Klient
                            <button class="ml-1 text-slate-400 hover:text-teal-600 transition">
                              <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" aria-hidden="true"
                                fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col"
                          class="px-2 py-2 font-semibold text-left text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          <span class="inline-flex items-center gap-1"
                            :class="{ 'text-teal-600': selectedColumn == 'company' }">
                            Spoločnosť
                            <button class="ml-1 text-slate-400 hover:text-teal-600 transition">
                              <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" aria-hidden="true"
                                fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col"
                          class="px-2 py-2 font-semibold text-left text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          <span class="inline-flex items-center"> Popis </span>
                        </th>
                        <th scope="col"
                          class="px-2 py-2 font-semibold text-left text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          <span class="inline-flex items-center"> Stav objednávky </span>
                        </th>
                        <th scope="col"
                          class="px-2 py-2 font-semibold text-left text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          <span class="inline-flex items-center"> Uhradené? </span>
                        </th>
                        <th scope="col"
                          class="px-6 py-4 text-xs font-semibold text-left text-slate-700 uppercase tracking-wider text-[12px]">
                          <span class="inline-flex items-center gap-1"
                            :class="{ 'text-teal-600': selectedColumn == 'date' }">
                            Dátum vytvorenia
                            <button class="ml-1 text-slate-400 hover:text-teal-600 transition">
                              <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" aria-hidden="true"
                                fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col"
                          class="px-2 py-2 font-semibold text-right text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          Celková suma</th>
                        <th scope="col"
                          class="px-2 py-2 font-semibold text-right text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          Zal. faktúra</th>
                        <th scope="col"
                          class="px-2 py-2 font-semibold text-right text-slate-700 uppercase tracking-wider whitespace-nowrap text-[12px]">
                          Faktúra</th>
                        <th scope="col"
                          class="px-2 py-2 font-semibold text-right text-slate-700 uppercase bg-white sticky right-0 z-20 shadow-lg tracking-wider whitespace-nowrap text-[12px]">
                          Upraviť</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                      <tr v-for="order in orders" :key="order.id"
                        :class="['hover:bg-teal-50/70', selectedOrderId === order.id ? 'bg-teal-100/80 ring-2 ring-teal-200' : '', 'transition-colors']">
                        <td v-if="canBulkStatusUpdate" class="px-2 py-2 text-center">
                          <input type="checkbox" :checked="selectedOrderIds.includes(order.id)"
                            @change="toggleOrderSelection(order.id, $event)"
                            class="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                        </td>
                        <td class="px-2 py-2 font-semibold text-slate-800 flex items-center gap-1 whitespace-nowrap">
                          <span>{{ order.id }}</span>
                          <button @click="copyOrderId(order.id)"
                            class="text-slate-400 hover:text-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-200 rounded transition"
                            title="Kopírovať ID">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                              stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M8 16h8M8 12h8m-7 8h6a2 2 0 002-2V6a2 2 0 00-2-2H9a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </td>
                        <td class="px-2 py-2 text-slate-700 truncate max-w-[110px] whitespace-nowrap"
                          :title="order.first_name + ' ' + order.last_name">
                          {{ order.first_name }} {{ order.last_name ? order.last_name : order.company_id }}
                        </td>
                        <td class="px-2 py-2 text-slate-700 truncate max-w-[110px] whitespace-nowrap"
                          :title="getCompanyName(order)">
                          {{ getCompanyName(order) }}
                        </td>
                        <td class="px-2 py-2 text-slate-700 truncate max-w-[120px] whitespace-nowrap"
                          :title="order.description">
                          {{ order.description }}
                        </td>
                        <td class="px-2 py-2">
                          <span
                            :class="getStatusBadgeClass(order.status) + ' shadow-sm px-3 py-1 rounded-full text-xs font-semibold'">{{
                              getOrderStatusName(order.status) }}</span>
                        </td>

                        <td class="px-2 py-2 text-slate-700 text-center">
                          <input disabled type="checkbox" :checked="order.is_paid" id="is_paid"
                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block shadow-sm sm:text-sm border-gray-300 rounded bg-white text-teal-500" />
                        </td>
                        <td class="px-2 py-2 text-slate-700 whitespace-nowrap">{{ formatDate(order.created_at ? order.created_at.toString() : order.updated_at.toString()) }}</td>
                        <td class="px-2 py-2 text-right text-slate-700 whitespace-nowrap font-medium">{{ Number(order.amount).toFixed(2) }} €</td>
                        <td class="px-2 py-2 font-medium text-right">
                          <button @click="downloadZFA(order.id)"
                            class="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-slate-50 text-teal-700 font-semibold hover:bg-teal-50 hover:text-teal-900 transition shadow-sm border border-gray-200">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                              stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                            </svg>
                            Stiahnuť
                          </button>
                        </td>
                        <td class="px-2 py-2 font-medium text-right">
                          <button @click="downloadFA(order.id)"
                            class="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-slate-50 text-teal-700 font-semibold hover:bg-teal-50 hover:text-teal-900 transition shadow-sm border border-gray-200">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                              stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                            </svg>
                            Stiahnuť
                          </button>
                        </td>
                        <td class="px-2 py-2 font-medium text-right bg-white sticky right-0 z-10 shadow-lg">
                          <router-link :to="'/order-detail/' + order.id"
                            class="inline-block px-4 py-1.5 rounded-lg bg-gradient-to-r from-green-100 to-teal-100 text-green-700 font-semibold hover:bg-green-200 hover:text-teal-900 transition shadow border border-green-200">Upraviť</router-link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div
                    class="flex items-center justify-between border-t border-gray-100 bg-white/95 px-6 py-4 sm:px-10 rounded-b-3xl">
                    <div class="flex flex-1 justify-between sm:hidden">
                      <button @click="retrieveOrders(pagination.current_page, 'prev')"
                        class="relative inline-flex items-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 transition disabled:opacity-50"
                        :disabled="pagination.current_page <= 1">
                        <svg class="h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                          fill="currentColor" aria-hidden="true">
                          <path fill-rule="evenodd"
                            d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                            clip-rule="evenodd" />
                        </svg>
                        Predchádzajúca
                      </button>
                      <button @click="retrieveOrders(pagination.current_page, 'next')"
                        class="relative ml-3 inline-flex items-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 transition disabled:opacity-50"
                        :disabled="pagination.current_page >= pagination.last_page">
                        Ďalšia
                        <svg class="h-5 w-5 ml-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                          fill="currentColor" aria-hidden="true">
                          <path fill-rule="evenodd"
                            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                            clip-rule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p class="text-sm text-slate-600">
                          Stránka
                          <span class="font-semibold text-teal-700">{{ pagination.current_page }}</span>
                          z
                          <span class="font-semibold text-teal-700">{{ pagination.last_page }}</span>
                        </p>
                      </div>
                      <div>
                        <nav class="isolate inline-flex gap-1 rounded-lg shadow-sm" aria-label="Pagination">
                          <button
                            class="relative inline-flex items-center rounded-l-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 focus:z-20 transition disabled:opacity-50"
                            :disabled="pagination.current_page <= 1"
                            @click="retrieveOrders(pagination.current_page, 'prev')">
                            <span class="sr-only">Predchádzajúca</span>
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>
                          <button v-for="pageNumber in pages" :key="pageNumber" @click="retrieveOrders(pageNumber, '')"
                            :class="[
                              'relative inline-flex items-center border px-4 py-2 text-sm font-medium',
                              pageNumber === pagination.current_page
                                ? 'z-10 bg-teal-50 border-teal-500 text-teal-700 font-bold shadow'
                                : 'border-gray-200 bg-white text-slate-500 hover:bg-slate-50 focus:z-20'
                            ]">
                            {{ pageNumber }}
                          </button>
                          <button
                            class="relative inline-flex items-center rounded-r-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 focus:z-20 transition disabled:opacity-50"
                            :disabled="pagination.current_page >= pagination.last_page"
                            @click="retrieveOrders(pagination.current_page, 'next')">
                            <span class="sr-only">Ďalšia</span>
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
